import { Menu, X } from "lucide-react";
import Link from "next/link";
import { useState, useEffect } from "react";

import { ctaButtonClassName } from "@components/bluetooth-landing/ctaButtonStyles";
import {
  getLanguageAlternate,
  type LanguageAlternate,
} from "@data/bluetooth-landing/languageAlternates";
import TextLogo from "@public/bluetooth-landing/current_color_text_logo.svg";

export interface NavbarCopy {
  links: {
    targetAudience: string;
    values: string;
    services: string;
    process: string;
    about: string;
    faq: string;
  };
  cta: string;
}

const defaultNavbarCopy: NavbarCopy = {
  links: {
    targetAudience: "Dla kogo",
    values: "Wartości",
    services: "Usługi",
    process: "Proces",
    about: "O nas",
    faq: "FAQ",
  },
  cta: "Wyceń projekt",
};

function LanguageSwitchLink({
  alternate,
  className,
  onClick,
}: {
  alternate: LanguageAlternate;
  className: string;
  onClick?: () => void;
}) {
  return (
    <a
      href={alternate.href}
      hrefLang={alternate.label === "EN" ? "en" : "pl"}
      onClick={onClick}
      className={className}
      aria-label={
        alternate.label === "EN" ? "English version" : "Polska wersja"
      }
    >
      {alternate.label}
    </a>
  );
}

export function Navbar({
  copy = defaultNavbarCopy,
  alternateLanguage,
}: {
  copy?: NavbarCopy;
  alternateLanguage?: LanguageAlternate;
}) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [resolvedAlternate, setResolvedAlternate] = useState<
    LanguageAlternate | undefined
  >(alternateLanguage);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (alternateLanguage) {
      setResolvedAlternate(alternateLanguage);
      return;
    }

    setResolvedAlternate(getLanguageAlternate(window.location.pathname));
  }, [alternateLanguage]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        mobileMenuOpen
          ? `bg-background shadow-lg ${isScrolled ? "py-4" : "py-6"}`
          : isScrolled
            ? "bg-background/95 backdrop-blur-md shadow-lg py-4"
            : "bg-transparent py-6"
      }`}
    >
      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        <div className="flex items-center justify-between">
          <Link
            href="/"
            className="text-white flex items-center shrink-0 overflow-visible"
            aria-label="Blues Brackets"
          >
            <TextLogo className="block w-32 md:w-40 h-[10.4px] md:h-[13px] overflow-visible text-white" />
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            <a
              href="#dla-kogo"
              className="text-sm font-medium text-slate-300 hover:text-white transition-colors"
            >
              {copy.links.targetAudience}
            </a>
            <a
              href="#wartosci"
              className="text-sm font-medium text-slate-300 hover:text-white transition-colors"
            >
              {copy.links.values}
            </a>
            <a
              href="#uslugi"
              className="text-sm font-medium text-slate-300 hover:text-white transition-colors"
            >
              {copy.links.services}
            </a>
            <a
              href="#proces"
              className="text-sm font-medium text-slate-300 hover:text-white transition-colors"
            >
              {copy.links.process}
            </a>
            <a
              href="#o-nas"
              className="text-sm font-medium text-slate-300 hover:text-white transition-colors"
            >
              {copy.links.about}
            </a>
            <a
              href="#faq"
              className="text-sm font-medium text-slate-300 hover:text-white transition-colors"
            >
              {copy.links.faq}
            </a>

            {resolvedAlternate && (
              <LanguageSwitchLink
                alternate={resolvedAlternate}
                className="text-sm font-semibold text-slate-300 hover:text-white transition-colors border border-slate-600 hover:border-slate-400 rounded-full px-3 py-1 min-w-[2.5rem] text-center"
              />
            )}

            <a
              href="#kontakt"
              className={ctaButtonClassName("text-sm py-2.5 px-6 rounded-full")}
            >
              {copy.cta}
            </a>
          </nav>

          <button
            type="button"
            className="md:hidden text-white p-2 relative"
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-nav-menu"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <Menu
              size={24}
              className={`transition-all duration-150 ${
                mobileMenuOpen
                  ? "scale-75 rotate-90 opacity-0"
                  : "scale-100 rotate-0 opacity-100"
              }`}
            />
            <X
              size={24}
              className={`absolute inset-0 m-auto transition-all duration-150 ${
                mobileMenuOpen
                  ? "scale-100 rotate-0 opacity-100"
                  : "scale-75 -rotate-90 opacity-0"
              }`}
            />
          </button>
        </div>
      </div>

      <div
        id="mobile-nav-menu"
        className={`md:hidden absolute top-full left-0 right-0 overflow-hidden transition-[opacity,transform,visibility] duration-150 ease-out ${
          mobileMenuOpen
            ? "opacity-100 translate-y-0 visible"
            : "opacity-0 -translate-y-2 invisible pointer-events-none"
        }`}
        aria-hidden={!mobileMenuOpen}
      >
        <div className="bg-background shadow-xl border-t border-slate-800">
          <div
            className={`flex flex-col p-6 gap-4 transition-opacity duration-150 ${
              mobileMenuOpen ? "opacity-100" : "opacity-0"
            }`}
          >
            <a
              href="#dla-kogo"
              onClick={() => setMobileMenuOpen(false)}
              className="text-slate-300 hover:text-white font-medium py-2 border-b border-slate-800"
            >
              {copy.links.targetAudience}
            </a>
            <a
              href="#wartosci"
              onClick={() => setMobileMenuOpen(false)}
              className="text-slate-300 hover:text-white font-medium py-2 border-b border-slate-800"
            >
              {copy.links.values}
            </a>
            <a
              href="#uslugi"
              onClick={() => setMobileMenuOpen(false)}
              className="text-slate-300 hover:text-white font-medium py-2 border-b border-slate-800"
            >
              {copy.links.services}
            </a>
            <a
              href="#proces"
              onClick={() => setMobileMenuOpen(false)}
              className="text-slate-300 hover:text-white font-medium py-2 border-b border-slate-800"
            >
              {copy.links.process}
            </a>
            <a
              href="#o-nas"
              onClick={() => setMobileMenuOpen(false)}
              className="text-slate-300 hover:text-white font-medium py-2 border-b border-slate-800"
            >
              {copy.links.about}
            </a>
            <a
              href="#faq"
              onClick={() => setMobileMenuOpen(false)}
              className="text-slate-300 hover:text-white font-medium py-2 border-b border-slate-800"
            >
              {copy.links.faq}
            </a>
            {resolvedAlternate && (
              <LanguageSwitchLink
                alternate={resolvedAlternate}
                onClick={() => setMobileMenuOpen(false)}
                className="text-slate-300 hover:text-white font-semibold py-2 border-b border-slate-800"
              />
            )}
            <a
              href="#kontakt"
              onClick={() => setMobileMenuOpen(false)}
              className={ctaButtonClassName(
                "font-semibold py-3 rounded-xl text-center mt-2",
              )}
            >
              {copy.cta}
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}
